import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { APIResultEventInfoWithConsentResponse, APIResultRegisterCompanyResponse, ConsentControllerService, EventInfoWithConsentResponse, EventPublicControllerService, RegisterCompanyControllerService, RegisterInvestorControllerService } from '@ent-regis/entregis-ts-angular';
import { APIResultNumber } from '@ent-regis/entregis-ts-angular/model/aPIResultNumber';
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { environment } from 'src/environments/environment';
import { LoginType } from '../constants/login-type.enum';
import { msg } from '../constants/msg';
import { AlertService } from '../shared/services/alert.service';
import { AuthService } from '../shared/services/auth.service';
import { LoginLayoutComponent } from '../layouts/login-layout/login-layout/login-layout.component';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  loginType: LoginType;
  eventAlias: string;
  eventInfo: EventInfoWithConsentResponse;
  LoginType = LoginType;
  eventTextWarning: SafeHtml = "End of event.";
  canLogin: boolean = false;
  isBeforeEvent: boolean = false;
  faSignOutAlt = faSignOutAlt;
  isCompany = false;

  today = new Date();


  form = new FormGroup({
    username: new FormControl('', Validators.required),
    password: new FormControl('', Validators.required)
  });

  constructor(
    private router: Router,
    private authService: AuthService,
    private registerCompanyService: RegisterCompanyControllerService,
    private registerInvestorService: RegisterInvestorControllerService,
    private consentService: ConsentControllerService,
    private activatedroute: ActivatedRoute,
    private eventPublicService: EventPublicControllerService,
    private alert: AlertService,
    private datePipe: DatePipe,
    private sanitizer:DomSanitizer,
    private loginLayout: LoginLayoutComponent,
  ) { }

  ngOnInit() {
    this.loginType = this.activatedroute.snapshot.data.loginType;
    this.isCompany = this.activatedroute.snapshot.data['loginType'] === LoginType.company;
    this.eventAlias = this.activatedroute.snapshot.paramMap.get('eventAlias');
    this.today.setHours(0, 0, 0, 0);
    // eventService fetch event data after login successfully
    // to this part will fetch event data separately
    this.eventPublicService.getInfoWithConsentUsingGET(this.eventAlias)
      .subscribe((response: APIResultEventInfoWithConsentResponse) => {
        this.eventInfo = response.data;
        this.loginLayout.setBackgroundUrl(response.data.bgUrl);
        if (this.eventInfo.eventType === EventInfoWithConsentResponse.EventTypeEnum.DIGITALROADSHOW && this.loginType == LoginType.company) {
          this.canLogin = false;
          this.eventTextWarning = this.sanitizer.bypassSecurityTrustHtml(`ท่านสามารถเข้าใช้งานระบบ SET Digital Roadshow ผ่านทางระบบ <a style="color:#007bff" href=${environment.setLink}> SETLink </a>`);
        } else {
          var eventStartDate = new Date(this.eventInfo.startDate);
          eventStartDate.setHours(0, 0, 0, 0)
          if (this.today < eventStartDate) {
            this.eventTextWarning = this.sanitizer.bypassSecurityTrustHtml("Event is not available.");
            this.isBeforeEvent = true;
          }
          if (this.eventInfo.isClose) {
            this.canLogin = this.isInPeriod(this.loginType, this.eventInfo);
          } else {
            this.canLogin = true;
          }
        }

      });

  }

  logout() { }

  login() {
    if (!this.form.valid) {
      this.alert.showError(msg.error.allRequiredData);
    } else {
      this.getUserLogin(() => {
        this.checkRole();
      });
    }
  }

  getUserLogin(onSuccess: (data: any) => void) {
    this.authService.getTokenWithUsername(this.form.get('username').value.trim(), this.form.get('password').value.trim(), this.loginType, this.eventInfo.eventId).subscribe(
      (res: any) => {
        const accessToken: string = res.access_token;
        const refreshToken: string = res.refresh_token;
        this.authService.updateToken(accessToken, refreshToken, this.eventAlias, this.loginType);
        onSuccess(res);
      },
      err => {
        this.alert.showError(msg.error.invalidLogin);
      }
    );

  }

  getPeriodString(eventInfo: EventInfoWithConsentResponse) {
    if (eventInfo) {
      const tz = eventInfo.eventType == 'THAILAND_FOCUS' ? 'UTC +7' : null;
      const startMonth = this.datePipe.transform(eventInfo.startDate, 'MMMM', tz)
      const endMonth = this.datePipe.transform(eventInfo.endDate, 'MMMM', tz)
      const year = this.datePipe.transform(eventInfo.endDate, 'yyyy', tz);
      const startDay = this.datePipe.transform(eventInfo.startDate, 'dd', tz)
      const endDay = this.datePipe.transform(eventInfo.endDate, 'dd', tz)
      const location = eventInfo.location ? ", " + eventInfo.location : "";
      if (startMonth == endMonth) {
        return `${startMonth} ${startDay}-${endDay}, ${year}${location}`;
      } else {
        return `${startMonth} ${startDay}-${endMonth} ${endDay}, ${year}${location}`;
      }
    }
  }

  formatEventPeriod(eventInfo: EventInfoWithConsentResponse): string {
    if (!eventInfo) return '';

    const timeZone = eventInfo.eventType === 'THAILAND_FOCUS' ? 'Asia/Bangkok': 'UTC';
    const formatter = new Intl.DateTimeFormat('en-US', {timeZone, month: 'short', day: 'numeric', year: 'numeric'});

    const getParts = (dateValue: string | Date) => {
      const parts = formatter.formatToParts(new Date(dateValue));
      return {
        month: parts.find(p => p.type === 'month')!.value,
        day: Number(parts.find(p => p.type === 'day')!.value),
        year: Number(parts.find(p => p.type === 'year')!.value)
      };
    };

    const start = getParts(this.loginType == LoginType.company ? eventInfo.startDateRegisterCompany : eventInfo.startDateRegisterInvestor);
    const end = getParts(this.loginType == LoginType.company ? eventInfo.endDateRegisterCompany : eventInfo.endDateRegisterInvestor);

    if (start.month === end.month && start.year === end.year) {
      return `${start.month} ${start.day}-${end.day}, ${start.year}`;
    }

    if (start.year === end.year) {
      return `${start.month} ${start.day} - ${end.month} ${end.day}, ${start.year}`;
    }

    return `${start.month} ${start.day}, ${start.year} - ${end.month} ${end.day}, ${end.year}`;
  }

  checkRole() {
    if (this.loginType === LoginType.company) {
      this.registerCompanyService.getRegisterCompanyByCurrentAppUserAndEventUsingGET(this.eventAlias).subscribe(
        (res: APIResultRegisterCompanyResponse) => {
          this.navigateCompanyAfterLogin(this.eventInfo.eventId, res.data.registerCompanyId);
        }, () => {
          this.alert.showError(msg.error.invalidLogin);
        }
      );

    } else if (this.loginType === LoginType.investor) {
      this.registerInvestorService.getRegisterInvestorIdUsingGET().subscribe(
        (res: APIResultNumber) => {
          this.navigateInvestorAfterLogin(this.eventInfo.eventId, res);
        }, () => {
          this.alert.showError(msg.error.invalidLogin);
        }
      );
    }
  }

  navigateCompanyAfterLogin(eventId: number, registerCompanyId: number) {
    this.consentService.getCheckAllActiveConsentUsingGET(eventId).subscribe(res => {
      const notAcceptedYetConsent = res.data.filter(x => x.didAccept === false);
      if (notAcceptedYetConsent.length > 0) {
        this.router.navigate(['regis', this.eventAlias, this.loginType, registerCompanyId, 'consent']);    
      } else {
        this.router.navigate(['regis', this.eventAlias, this.loginType, registerCompanyId]);
      }
    });
  }

  navigateInvestorAfterLogin(eventId: number, investor: APIResultNumber) {
    this.consentService.getCheckAllActiveConsentUsingGET(eventId).subscribe(res => {
      const notAcceptedYetConsent = res.data.filter(x => x.didAccept === false);
      if (notAcceptedYetConsent.length > 0) {
        this.router.navigate(['regis', this.eventAlias, this.loginType, investor.data, 'consent']);
      } else {
        this.router.navigate(['regis', this.eventAlias, this.loginType, investor.data]);
      }
    });
  }

  isInPeriod(loginType: LoginType, eventInfo: EventInfoWithConsentResponse): boolean {
    var regisStartDate: Date;
    var regisEndDate: Date;
    if (loginType == LoginType.company) {
      regisStartDate = new Date(eventInfo.startDateRegisterCompany);
      regisEndDate = new Date(eventInfo.endDateRegisterCompany);
      regisStartDate.setHours(0, 0, 0, 0)
      regisEndDate.setHours(0, 0, 0, 0)
    } else {
      regisStartDate = new Date(eventInfo.startDateRegisterInvestor);
      regisStartDate.setHours(0, 0, 0, 0)
      regisEndDate = new Date(eventInfo.endDateRegisterInvestor);
      regisEndDate.setHours(0, 0, 0, 0)
    }
    return (regisStartDate <= this.today && this.today <= regisEndDate) ||
      (regisEndDate < this.today && this.isBeforeEvent);
  }
}
